// @format
import { Device, Call } from "@twilio/voice-sdk";

type CallParams = Record<string, string>;

let device: Device | null = null;
let activeCall: Call | null = null;
let tokenRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

export async function initTwilioDevice(): Promise<Device | null> {
  if (device) {
    return device;
  }

  try {
    const response = await $.ajax({
      url: "/calls/token",
      method: "GET",
    });

    device = new Device(response.token, {
      logLevel: 1,
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
    });

    device.on("registered", () => {
      console.log("Twilio Device registered");
    });

    device.on("error", (error) => {
      console.error("Twilio Device error:", error);
    });

    device.on("incoming", (call) => {
      call.reject();
    });

    await device.register();

    scheduleTokenRefresh(45 * 60 * 1000);

    return device;
  } catch (error) {
    console.error("Failed to initialize Twilio Device:", error);
    return null;
  }
}

function scheduleTokenRefresh(delay: number): void {
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
  }

  tokenRefreshTimeout = setTimeout(async () => {
    try {
      const response = await $.ajax({
        url: "/calls/token",
        method: "GET",
      });

      if (device) {
        device.updateToken(response.token);
        scheduleTokenRefresh(45 * 60 * 1000);
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
  }, delay);
}

export async function makeCall(params: CallParams): Promise<Call | null> {
  if (!device) {
    device = await initTwilioDevice();
  }

  if (!device) {
    throw new Error("Twilio Device not available");
  }

  activeCall = await device.connect({ params });

  return activeCall;
}

export function hangupCall(): void {
  if (activeCall) {
    activeCall.disconnect();
    activeCall = null;
  }
}

export function getActiveCall(): Call | null {
  return activeCall;
}

export function muteCall(mute: boolean): void {
  if (activeCall) {
    activeCall.mute(mute);
  }
}

export function isCallMuted(): boolean {
  return activeCall ? activeCall.isMuted() : false;
}

export function destroyDevice(): void {
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
  }
  if (device) {
    device.destroy();
    device = null;
  }
  activeCall = null;
}
